import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { RelativeTime } from '@/components/ui/relative-time'
import { Globe, RefreshCw } from 'lucide-react'
import type { Project, ActiveWorkflow } from '@/types/conversations'
import { isWorkflowInProgress } from '@/lib/workflow-utils'

export function getDisplayUrl(project: Project): string | null {
  if (project.custom_domain && project.custom_domain_status === 'active') {
    return `https://${project.custom_domain}`
  }
  if (project.vanity_url) {
    return project.vanity_url
  }
  return project.production_url
}

interface LiveStatusBarProps {
  project: Project
  processing: boolean
  onDeploy: () => void
  onRedeploy: () => void
  onRestart: () => void
  activeWorkflow?: ActiveWorkflow | null
}

export function LiveStatusBar({
  project,
  processing,
  onDeploy,
  onRedeploy,
  onRestart,
  activeWorkflow,
}: LiveStatusBarProps) {
  const { t } = useTranslation('conversations')
  const isSuspendedNoCredits = project.production_status === 'suspended_no_credits'
  const deployWorkflowActive = isWorkflowInProgress(activeWorkflow)
  const isDeploying = project.production_status === 'deploying' || deployWorkflowActive
  const isDeployed = project.production_status === 'deployed' && !deployWorkflowActive
  const isFailed = project.production_status === 'deploy_failed' && !deployWorkflowActive

  const isAlreadyLive =
    !isSuspendedNoCredits &&
    project.deployed_history_entry_id != null &&
    project.deployed_history_entry_id === project.current_history_entry_id

  const isSandboxStopped = project.runtime_state !== 'running'

  const isDisabled =
    processing ||
    isSandboxStopped ||
    !project.all_integrations_have_live_vars ||
    isDeploying ||
    isAlreadyLive

  const displayUrl = getDisplayUrl(project)

  const hasDeployTime = !!project.production_deployed_at

  // Status dot color
  const dotColor = isDeployed
    ? 'bg-green-500'
    : isFailed
      ? 'bg-red-500'
      : isDeploying
        ? 'bg-blue-500 animate-pulse'
        : isSuspendedNoCredits
          ? 'bg-amber-500'
          : 'bg-gray-400'

  // Status label
  const statusLabel = isDeploying
    ? t('live.sre.statusBar.deploying')
    : isFailed
      ? t('live.sre.statusBar.deployFailed')
      : isSuspendedNoCredits
        ? t('live.sre.statusBar.suspended')
        : isDeployed
          ? t('live.sre.statusBar.live')
          : t('live.sre.statusBar.notDeployed')

  // Deploy button label
  const buttonLabel = isSuspendedNoCredits
    ? t('live.buttons.restart')
    : project.production_url
      ? t('live.buttons.redeploy')
      : t('live.buttons.deploy')

  const handleClick = isSuspendedNoCredits
    ? onRestart
    : project.production_url
      ? onRedeploy
      : onDeploy

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border bg-card px-4 py-2.5">
      {/* Status dot + label */}
      <div className="flex items-center gap-2">
        <div className={`h-2.5 w-2.5 rounded-full ${dotColor}`} />
        <span className="text-sm font-medium">{statusLabel}</span>
      </div>

      {/* Production URL */}
      {displayUrl && (
        <a
          href={displayUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-mono text-muted-foreground hover:text-foreground truncate max-w-[200px]"
        >
          {displayUrl.replace(/^https?:\/\//, '')}
        </a>
      )}

      {/* Deployed time */}
      {hasDeployTime && (
        <RelativeTime
          timestamp={project.production_deployed_at!}
          className="text-xs text-muted-foreground hidden sm:inline"
        />
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Deploy button */}
      <Button
        size="sm"
        onClick={handleClick}
        disabled={isDisabled}
        title={isSandboxStopped ? t('live.deployment.sandboxStopped') : isAlreadyLive ? t('live.deployment.alreadyLive') : undefined}
      >
        {isDeploying ? (
          <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        ) : (
          <Globe className="h-3.5 w-3.5 mr-1.5" />
        )}
        {buttonLabel}
      </Button>
    </div>
  )
}

